// ─── Shared Data Store ────────────────────────────────────────────────────────
// Single source of truth for all mock data across the application.
// Dashboard, Reporting, Locaux, Enseignes, Matching, Visites, Documents
// all derive their statistics from these shared arrays.

// ─── Types ────────────────────────────────────────────────────────────────────

export type Property = {
  id: string;
  ref: string;
  title: string;
  city: string;
  quartier: string;
  address: string;
  type: string;
  surfaceTotal: number;
  surfaceRDC: number;
  loyer: number;
  status: string;
  completude: number;
  broker: string;
  nbVisites: number;
  bestMatchScore: number;
  bestMatchTenant: string;
  disponibilite: string;
  facade: number;
  hauteurPlafond: number;
  proprietaire: string;
  updatedAt: string;
  hasPhotos: boolean;
  hasDocs: boolean;
  lat: number;
  lng: number;
};

export type Enseigne = {
  id: string;
  nom: string;
  raisonSociale: string;
  secteur: string;
  statut: string;
  villesCibles: string[];
  surfaceMin: number;
  surfaceMax: number;
  budgetMax: number;
  facade: number;
  contact: string;
  nbLocaux: number;
  score: number;
  updatedAt: string;
};

export type Visite = {
  id: string;
  local: string;
  localRef: string;
  enseigne: string;
  contact: string;
  date: string;
  heure: string;
  broker: string;
  statut: 'Planifiée' | 'Réalisée' | 'Annulée' | 'À reprogrammer';
  note: string;
  priorite: 'Haute' | 'Normale' | 'Basse';
};

export type Document = {
  id: string;
  nom: string;
  type: 'Juridique' | 'Technique' | 'Médias' | 'Administratif' | 'Commercial';
  local: string;
  localRef: string;
  statut: 'Validé' | 'À vérifier' | 'Manquant' | 'Reçu' | 'Expiré';
  uploadedBy: string;
  date: string;
  taille: string;
  version: number;
  permissions: string[];
};

export type MatchResult = {
  localRef: string;
  localTitre: string;
  localVille: string;
  localSurface: number;
  localLoyer: number;
  localFacade: number;
  localDisponibilite: string;
  enseigne: string;
  secteur: string;
  scoreGlobal: number;
  scoreLocalisation: number;
  scoreSurface: number;
  scoreFacade: number;
  scoreBudget: number;
  scoreTechnique: number;
  scoreTiming: number;
  statut: string;
};

// ─── Locaux ───────────────────────────────────────────────────────────────────

export const ALL_PROPERTIES: Property[] = [
  {
    id: '1', ref: 'LOC-0247', title: 'Avenue Anfa — Angle rue Molière', city: 'Casablanca', quartier: 'Maarif',
    address: '45 Avenue Anfa, angle rue Molière', type: 'Angle de rue', surfaceTotal: 420, surfaceRDC: 280,
    loyer: 85000, status: 'Publié', completude: 92, broker: 'K. Alami', nbVisites: 4,
    bestMatchScore: 94, bestMatchTenant: 'Zara Home', disponibilite: 'Immédiate',
    facade: 12, hauteurPlafond: 4.2, proprietaire: 'SCI Anfa Invest', updatedAt: '15 mars 2026',
    hasPhotos: true, hasDocs: true, lat: 33.5731, lng: -7.6298,
  },
  {
    id: '2', ref: 'LOC-0248', title: 'Boulevard Mohammed V — RDC', city: 'Rabat', quartier: 'Centre-Ville',
    address: '12 Blvd Mohammed V', type: 'Rue principale', surfaceTotal: 280, surfaceRDC: 280,
    loyer: 52000, status: 'Visité', completude: 78, broker: 'S. Benali', nbVisites: 2,
    bestMatchScore: 91, bestMatchTenant: "McDonald's", disponibilite: 'Immédiate',
    facade: 9, hauteurPlafond: 3.8, proprietaire: 'Foncière Royale', updatedAt: '18 mars 2026',
    hasPhotos: true, hasDocs: false, lat: 34.0209, lng: -6.8416,
  },
  {
    id: '3', ref: 'LOC-0231', title: 'Mall Anfa Place — Niveau 0', city: 'Casablanca', quartier: 'Ain Diab',
    address: 'Mall Anfa Place, Local 0-42', type: 'Centre commercial', surfaceTotal: 195, surfaceRDC: 195,
    loyer: 48000, status: 'En discussion', completude: 95, broker: 'K. Alami', nbVisites: 7,
    bestMatchScore: 88, bestMatchTenant: 'Pharmacie Centrale', disponibilite: '01/06/2026',
    facade: 7, hauteurPlafond: 3.5, proprietaire: 'Anfa Place SARL', updatedAt: '19 mars 2026',
    hasPhotos: true, hasDocs: true, lat: 33.5892, lng: -7.6614,
  },
  {
    id: '4', ref: 'LOC-0219', title: 'Rue de la Liberté — Guéliz', city: 'Marrakech', quartier: 'Guéliz',
    address: '78 Rue de la Liberté', type: 'Rue principale', surfaceTotal: 340, surfaceRDC: 220,
    loyer: 62000, status: 'Offre reçue', completude: 88, broker: 'N. Tahiri', nbVisites: 5,
    bestMatchScore: 85, bestMatchTenant: 'Carrefour Express', disponibilite: 'Immédiate',
    facade: 11, hauteurPlafond: 4.0, proprietaire: 'Groupe Lazari', updatedAt: '12 mars 2026',
    hasPhotos: true, hasDocs: true, lat: 31.6295, lng: -7.9811,
  },
  {
    id: '5', ref: 'LOC-0198', title: 'Avenue Hassan II — Angle', city: 'Fès', quartier: 'Centre',
    address: '3 Avenue Hassan II', type: 'Angle de rue', surfaceTotal: 160, surfaceRDC: 160,
    loyer: 28000, status: 'Publié', completude: 41, broker: 'M. Ouali', nbVisites: 1,
    bestMatchScore: 79, bestMatchTenant: 'Barid Bank', disponibilite: 'Immédiate',
    facade: 6, hauteurPlafond: 3.2, proprietaire: 'Propriétaire privé', updatedAt: '15 fév 2026',
    hasPhotos: false, hasDocs: false, lat: 34.0331, lng: -5.0003,
  },
  {
    id: '6', ref: 'LOC-0251', title: 'Hay Riad — Résidence Atlas', city: 'Rabat', quartier: 'Hay Riad',
    address: '22 Avenue Fal Ould Oumeir', type: "Pied d'immeuble", surfaceTotal: 380, surfaceRDC: 250,
    loyer: 72000, status: 'En revue', completude: 55, broker: 'N. Tahiri', nbVisites: 0,
    bestMatchScore: 74, bestMatchTenant: 'Zara', disponibilite: '01/04/2026',
    facade: 10, hauteurPlafond: 3.6, proprietaire: 'CGEM Immobilier', updatedAt: '19 mars 2026',
    hasPhotos: true, hasDocs: false, lat: 33.9716, lng: -6.8498,
  },
  {
    id: '7', ref: 'LOC-0203', title: 'Boulevard Massira Al Khadra', city: 'Casablanca', quartier: 'Sidi Maarouf',
    address: '156 Blvd Massira Al Khadra', type: 'Rue principale', surfaceTotal: 510, surfaceRDC: 350,
    loyer: 95000, status: 'À fort potentiel', completude: 84, broker: 'K. Alami', nbVisites: 3,
    bestMatchScore: 82, bestMatchTenant: 'Decathlon', disponibilite: '15/05/2026',
    facade: 18, hauteurPlafond: 5.5, proprietaire: 'Massira Invest', updatedAt: '10 mars 2026',
    hasPhotos: true, hasDocs: true, lat: 33.5417, lng: -7.6581,
  },
  {
    id: '8', ref: 'LOC-0237', title: 'Marina Casablanca — Rez-de-chaussée', city: 'Casablanca', quartier: 'Marina',
    address: 'Marina Casablanca, Lot C-12', type: 'Centre commercial', surfaceTotal: 245, surfaceRDC: 245,
    loyer: 44000, status: 'Publié', completude: 71, broker: 'S. Benali', nbVisites: 2,
    bestMatchScore: 76, bestMatchTenant: 'Starbucks', disponibilite: 'Immédiate',
    facade: 8, hauteurPlafond: 3.4, proprietaire: 'Marina SA', updatedAt: '17 mars 2026',
    hasPhotos: true, hasDocs: true, lat: 33.6037, lng: -7.6322,
  },
  {
    id: '9', ref: 'LOC-0189', title: 'Agdal — Rue Oued Fès', city: 'Rabat', quartier: 'Agdal',
    address: '34 Rue Oued Fès', type: "Pied d'immeuble", surfaceTotal: 130, surfaceRDC: 130,
    loyer: 22000, status: 'Brouillon', completude: 30, broker: 'M. Ouali', nbVisites: 0,
    bestMatchScore: 0, bestMatchTenant: '—', disponibilite: 'Inconnue',
    facade: 5, hauteurPlafond: 2.9, proprietaire: 'Propriétaire privé', updatedAt: '02 mars 2026',
    hasPhotos: false, hasDocs: false, lat: 33.9919, lng: -6.8526,
  },
  {
    id: '10', ref: 'LOC-0244', title: 'Tanger City Center — Niveau 1', city: 'Tanger', quartier: 'Centre',
    address: 'Tanger City Center, Local 1-08', type: 'Centre commercial', surfaceTotal: 310, surfaceRDC: 310,
    loyer: 58000, status: 'Publié', completude: 89, broker: 'K. Alami', nbVisites: 3,
    bestMatchScore: 83, bestMatchTenant: 'H&M', disponibilite: '01/07/2026',
    facade: 10, hauteurPlafond: 3.8, proprietaire: 'TCC Invest', updatedAt: '14 mars 2026',
    hasPhotos: true, hasDocs: true, lat: 35.7595, lng: -5.8340,
  },
  {
    id: '11', ref: 'LOC-0228', title: 'Corniche Aïn Diab — Façade mer', city: 'Casablanca', quartier: 'Ain Diab',
    address: '210 Boulevard de la Corniche', type: 'Rue principale', surfaceTotal: 480, surfaceRDC: 320,
    loyer: 110000, status: 'En négociation', completude: 97, broker: 'S. Benali', nbVisites: 8,
    bestMatchScore: 90, bestMatchTenant: 'Café de Flore', disponibilite: '01/09/2026',
    facade: 22, hauteurPlafond: 4.8, proprietaire: 'Corniche Premium SARL', updatedAt: '19 mars 2026',
    hasPhotos: true, hasDocs: true, lat: 33.5862, lng: -7.6742,
  },
  {
    id: '12', ref: 'LOC-0255', title: 'Palmeraie Mall — RDC Aile Nord', city: 'Marrakech', quartier: 'Palmeraie',
    address: 'Palmeraie Mall, Aile Nord, Local N-05', type: 'Centre commercial', surfaceTotal: 220, surfaceRDC: 220,
    loyer: 42000, status: 'Publié', completude: 66, broker: 'N. Tahiri', nbVisites: 1,
    bestMatchScore: 71, bestMatchTenant: 'Yves Rocher', disponibilite: 'Immédiate',
    facade: 7, hauteurPlafond: 3.3, proprietaire: 'Palmeraie Group', updatedAt: '08 mars 2026',
    hasPhotos: true, hasDocs: false, lat: 31.6860, lng: -8.0089,
  },
];

// ─── Runtime mutations ────────────────────────────────────────────────────────
// These functions mutate the shared arrays so that changes made in one page
// (e.g. adding a local via /locaux/nouveau) are immediately visible in others.

export function addProperty(property: Property): void {
  ALL_PROPERTIES.unshift(property);
}

export function removeProperty(id: string): void {
  const idx = ALL_PROPERTIES.findIndex(p => p.id === id);
  if (idx !== -1) ALL_PROPERTIES.splice(idx, 1);
}

// ─── Enseignes ────────────────────────────────────────────────────────────────

export const ALL_ENSEIGNES: Enseigne[] = [
  { id: '1', nom: 'Zara Home', raisonSociale: 'Inditex Maroc SARL', secteur: 'Décoration & Maison', statut: 'En recherche', villesCibles: ['Casablanca', 'Rabat', 'Marrakech'], surfaceMin: 300, surfaceMax: 600, budgetMax: 100000, facade: 10, contact: 'Marie Dupont', nbLocaux: 3, score: 94, updatedAt: '19 mars 2026' },
  { id: '2', nom: "McDonald's", raisonSociale: "McDonald's Maroc SA", secteur: 'Restauration rapide', statut: 'Actif', villesCibles: ['Casablanca', 'Rabat', 'Tanger', 'Fès'], surfaceMin: 200, surfaceMax: 400, budgetMax: 70000, facade: 8, contact: 'Jean Martin', nbLocaux: 5, score: 91, updatedAt: '18 mars 2026' },
  { id: '3', nom: 'Pharmacie Centrale', raisonSociale: 'Groupe Pharma Maroc', secteur: 'Santé & Beauté', statut: 'En visite', villesCibles: ['Casablanca', 'Rabat'], surfaceMin: 150, surfaceMax: 300, budgetMax: 55000, facade: 6, contact: 'Dr. Amina Raji', nbLocaux: 2, score: 88, updatedAt: '19 mars 2026' },
  { id: '4', nom: 'Carrefour Express', raisonSociale: 'Carrefour Maroc SA', secteur: 'Grande distribution', statut: 'En recherche', villesCibles: ['Casablanca', 'Marrakech', 'Agadir'], surfaceMin: 250, surfaceMax: 500, budgetMax: 80000, facade: 12, contact: 'Pierre Blanc', nbLocaux: 4, score: 85, updatedAt: '12 mars 2026' },
  { id: '5', nom: 'Starbucks', raisonSociale: 'Starbucks Coffee Maroc', secteur: 'Café & Restauration', statut: 'Actif', villesCibles: ['Casablanca', 'Rabat', 'Marrakech', 'Tanger'], surfaceMin: 150, surfaceMax: 300, budgetMax: 60000, facade: 7, contact: 'Laura Smith', nbLocaux: 6, score: 83, updatedAt: '17 mars 2026' },
  { id: '6', nom: 'H&M', raisonSociale: 'H&M Maroc SARL', secteur: 'Mode & Textile', statut: 'En négociation', villesCibles: ['Casablanca', 'Rabat', 'Tanger'], surfaceMin: 400, surfaceMax: 800, budgetMax: 120000, facade: 15, contact: 'Anna Karlsson', nbLocaux: 2, score: 82, updatedAt: '14 mars 2026' },
  { id: '7', nom: 'Decathlon', raisonSociale: 'Decathlon Maroc SA', secteur: 'Sport & Loisirs', statut: 'Prospect', villesCibles: ['Casablanca', 'Rabat'], surfaceMin: 800, surfaceMax: 2000, budgetMax: 150000, facade: 20, contact: 'Marc Leroy', nbLocaux: 1, score: 79, updatedAt: '10 mars 2026' },
  { id: '8', nom: 'Yves Rocher', raisonSociale: 'Yves Rocher Maroc', secteur: 'Beauté & Cosmétiques', statut: 'Inactif', villesCibles: ['Casablanca', 'Marrakech'], surfaceMin: 80, surfaceMax: 180, budgetMax: 35000, facade: 5, contact: 'Sophie Moreau', nbLocaux: 0, score: 71, updatedAt: '08 mars 2026' },
];

// ─── Visites ──────────────────────────────────────────────────────────────────

export const ALL_VISITES: Visite[] = [
  { id: '1', local: 'Avenue Anfa — Angle rue Molière', localRef: 'LOC-0247', enseigne: 'Zara Home', contact: 'Marie Dupont', date: '25 mars 2026', heure: '10:00', broker: 'K. Alami', statut: 'Planifiée', note: 'Apporter les plans détaillés', priorite: 'Haute' },
  { id: '2', local: 'Mall Anfa Place — Niveau 0', localRef: 'LOC-0231', enseigne: 'Pharmacie Centrale', contact: 'Dr. Amina Raji', date: '26 mars 2026', heure: '14:30', broker: 'K. Alami', statut: 'Planifiée', note: '', priorite: 'Normale' },
  { id: '3', local: 'Corniche Aïn Diab — Façade mer', localRef: 'LOC-0228', enseigne: 'Café de Flore', contact: 'Thomas Renard', date: '27 mars 2026', heure: '11:00', broker: 'S. Benali', statut: 'Planifiée', note: 'Visite avec le DG', priorite: 'Haute' },
  { id: '4', local: 'Boulevard Mohammed V — RDC', localRef: 'LOC-0248', enseigne: "McDonald's", contact: 'Jean Martin', date: '12 mars 2026', heure: '09:00', broker: 'S. Benali', statut: 'Réalisée', note: 'Très intéressé, demande devis travaux', priorite: 'Haute' },
  { id: '5', local: 'Avenue Anfa — Angle rue Molière', localRef: 'LOC-0247', enseigne: 'H&M Home', contact: 'Anna Karlsson', date: '08 mars 2026', heure: '15:00', broker: 'K. Alami', statut: 'Réalisée', note: 'Surface OK, négocie le loyer -10%', priorite: 'Normale' },
  { id: '6', local: 'Rue de la Liberté — Guéliz', localRef: 'LOC-0219', enseigne: 'Carrefour Express', contact: 'Pierre Blanc', date: '05 mars 2026', heure: '10:30', broker: 'N. Tahiri', statut: 'Annulée', note: 'Contact injoignable, à reprogrammer', priorite: 'Normale' },
  { id: '7', local: 'Tanger City Center — Niveau 1', localRef: 'LOC-0244', enseigne: 'H&M', contact: 'Anna Karlsson', date: '03 mars 2026', heure: '14:00', broker: 'K. Alami', statut: 'Annulée', note: 'Déplacement annulé côté enseigne', priorite: 'Basse' },
  { id: '8', local: 'Hay Riad — Résidence Atlas', localRef: 'LOC-0251', enseigne: 'Zara', contact: 'Sophie Moreau', date: '01 mars 2026', heure: '11:00', broker: 'N. Tahiri', statut: 'À reprogrammer', note: 'Demande de report à avril', priorite: 'Haute' },
  { id: '9', local: 'Rue de la Liberté — Guéliz', localRef: 'LOC-0219', enseigne: 'Carrefour Express', contact: 'Pierre Blanc', date: '19 mars 2026', heure: '09:30', broker: 'N. Tahiri', statut: 'Réalisée', note: 'Très bonne impression, offre en cours', priorite: 'Haute' },
  { id: '10', local: 'Boulevard Massira Al Khadra', localRef: 'LOC-0203', enseigne: 'Decathlon', contact: 'Marc Leroy', date: '20 mars 2026', heure: '14:00', broker: 'K. Alami', statut: 'Planifiée', note: 'Première visite', priorite: 'Normale' },
  { id: '11', local: 'Palmeraie Mall — RDC Aile Nord', localRef: 'LOC-0255', enseigne: 'Yves Rocher', contact: 'Sophie Moreau', date: '15 mars 2026', heure: '11:00', broker: 'N. Tahiri', statut: 'Réalisée', note: 'Surface un peu petite', priorite: 'Basse' },
];

// ─── Documents ────────────────────────────────────────────────────────────────

export const ALL_DOCUMENTS: Document[] = [
  { id: '1', nom: 'Titre foncier TF-12847/C', type: 'Juridique', local: 'Avenue Anfa — Angle rue Molière', localRef: 'LOC-0247', statut: 'Validé', uploadedBy: 'K. Alami', date: '10 jan 2026', taille: '2.4 Mo', version: 2, permissions: ['Admin', 'Broker', 'Propriétaire'] },
  { id: '2', nom: 'Plan RDC + Mezzanine', type: 'Technique', local: 'Avenue Anfa — Angle rue Molière', localRef: 'LOC-0247', statut: 'Validé', uploadedBy: 'K. Alami', date: '15 jan 2026', taille: '8.1 Mo', version: 1, permissions: ['Admin', 'Broker', 'Locataire'] },
  { id: '3', nom: 'Diagnostic électrique', type: 'Technique', local: 'Avenue Anfa — Angle rue Molière', localRef: 'LOC-0247', statut: 'À vérifier', uploadedBy: 'S. Benali', date: '01 fév 2026', taille: '1.2 Mo', version: 1, permissions: ['Admin', 'Broker'] },
  { id: '4', nom: 'Bail commercial modèle', type: 'Juridique', local: 'Avenue Anfa — Angle rue Molière', localRef: 'LOC-0247', statut: 'Manquant', uploadedBy: '—', date: '—', taille: '—', version: 0, permissions: ['Admin', 'Broker'] },
  { id: '5', nom: 'Photos façade (12 photos)', type: 'Médias', local: 'Avenue Anfa — Angle rue Molière', localRef: 'LOC-0247', statut: 'Validé', uploadedBy: 'K. Alami', date: '20 jan 2026', taille: '45 Mo', version: 3, permissions: ['Admin', 'Broker', 'Locataire', 'Visiteur'] },
  { id: '6', nom: 'Titre foncier TF-9823/R', type: 'Juridique', local: 'Boulevard Mohammed V — RDC', localRef: 'LOC-0248', statut: 'Validé', uploadedBy: 'S. Benali', date: '05 fév 2026', taille: '1.8 Mo', version: 1, permissions: ['Admin', 'Broker', 'Propriétaire'] },
  { id: '7', nom: "Plan d'aménagement", type: 'Technique', local: 'Mall Anfa Place — Niveau 0', localRef: 'LOC-0231', statut: 'Reçu', uploadedBy: 'K. Alami', date: '18 mars 2026', taille: '12.3 Mo', version: 1, permissions: ['Admin', 'Broker', 'Locataire'] },
  { id: '8', nom: "Autorisation d'exploitation", type: 'Administratif', local: 'Mall Anfa Place — Niveau 0', localRef: 'LOC-0231', statut: 'Expiré', uploadedBy: 'K. Alami', date: '01 jan 2025', taille: '0.8 Mo', version: 1, permissions: ['Admin', 'Broker'] },
  { id: '9', nom: 'Offre commerciale Zara Home', type: 'Commercial', local: 'Avenue Anfa — Angle rue Molière', localRef: 'LOC-0247', statut: 'Reçu', uploadedBy: 'K. Alami', date: '19 mars 2026', taille: '0.5 Mo', version: 1, permissions: ['Admin', 'Broker'] },
  { id: '10', nom: "Rapport d'expertise immobilière", type: 'Commercial', local: 'Corniche Aïn Diab — Façade mer', localRef: 'LOC-0228', statut: 'Validé', uploadedBy: 'S. Benali', date: '10 mars 2026', taille: '3.2 Mo', version: 1, permissions: ['Admin', 'Broker', 'Propriétaire'] },
  { id: '11', nom: 'Titre foncier TF-7741/M', type: 'Juridique', local: 'Rue de la Liberté — Guéliz', localRef: 'LOC-0219', statut: 'Manquant', uploadedBy: '—', date: '—', taille: '—', version: 0, permissions: ['Admin', 'Broker'] },
  { id: '12', nom: 'Photos intérieur', type: 'Médias', local: 'Tanger City Center — Niveau 1', localRef: 'LOC-0244', statut: 'Validé', uploadedBy: 'K. Alami', date: '12 mars 2026', taille: '22 Mo', version: 1, permissions: ['Admin', 'Broker', 'Locataire', 'Visiteur'] },
];

// ─── Matching ─────────────────────────────────────────────────────────────────

export const ALL_MATCHES: MatchResult[] = [
  { localRef: 'LOC-0247', localTitre: 'Avenue Anfa — Angle rue Molière', localVille: 'Casablanca', localSurface: 420, localLoyer: 85000, localFacade: 12, localDisponibilite: 'Immédiate', enseigne: 'Zara Home', secteur: 'Décoration', scoreGlobal: 94, scoreLocalisation: 100, scoreSurface: 95, scoreFacade: 92, scoreBudget: 90, scoreTechnique: 95, scoreTiming: 95, statut: 'En discussion' },
  { localRef: 'LOC-0248', localTitre: 'Boulevard Mohammed V — RDC', localVille: 'Rabat', localSurface: 280, localLoyer: 52000, localFacade: 9, localDisponibilite: 'Immédiate', enseigne: "McDonald's", secteur: 'Restauration', scoreGlobal: 91, scoreLocalisation: 100, scoreSurface: 88, scoreFacade: 85, scoreBudget: 95, scoreTechnique: 90, scoreTiming: 90, statut: 'Contacté' },
  { localRef: 'LOC-0231', localTitre: 'Mall Anfa Place — Niveau 0', localVille: 'Casablanca', localSurface: 195, localLoyer: 48000, localFacade: 7, localDisponibilite: '01/06/2026', enseigne: 'Pharmacie Centrale', secteur: 'Santé', scoreGlobal: 88, scoreLocalisation: 100, scoreSurface: 90, scoreFacade: 80, scoreBudget: 88, scoreTechnique: 85, scoreTiming: 82, statut: 'Planifié' },
  { localRef: 'LOC-0219', localTitre: 'Rue de la Liberté — Guéliz', localVille: 'Marrakech', localSurface: 340, localLoyer: 62000, localFacade: 11, localDisponibilite: 'Immédiate', enseigne: 'Carrefour Express', secteur: 'Distribution', scoreGlobal: 85, scoreLocalisation: 100, scoreSurface: 82, scoreFacade: 88, scoreBudget: 80, scoreTechnique: 82, scoreTiming: 85, statut: 'À contacter' },
  { localRef: 'LOC-0203', localTitre: 'Boulevard Massira Al Khadra', localVille: 'Casablanca', localSurface: 510, localLoyer: 95000, localFacade: 18, localDisponibilite: '15/05/2026', enseigne: 'Decathlon', secteur: 'Sport', scoreGlobal: 82, scoreLocalisation: 100, scoreSurface: 85, scoreFacade: 95, scoreBudget: 72, scoreTechnique: 88, scoreTiming: 78, statut: 'À contacter' },
  { localRef: 'LOC-0244', localTitre: 'Tanger City Center — Niveau 1', localVille: 'Tanger', localSurface: 310, localLoyer: 58000, localFacade: 10, localDisponibilite: '01/07/2026', enseigne: 'H&M', secteur: 'Mode', scoreGlobal: 83, scoreLocalisation: 100, scoreSurface: 78, scoreFacade: 80, scoreBudget: 85, scoreTechnique: 82, scoreTiming: 75, statut: 'À contacter' },
  { localRef: 'LOC-0228', localTitre: 'Corniche Aïn Diab — Façade mer', localVille: 'Casablanca', localSurface: 480, localLoyer: 110000, localFacade: 22, localDisponibilite: '01/09/2026', enseigne: 'Café de Flore', secteur: 'Café & Restauration', scoreGlobal: 90, scoreLocalisation: 100, scoreSurface: 92, scoreFacade: 98, scoreBudget: 78, scoreTechnique: 88, scoreTiming: 82, statut: 'En négociation' },
  { localRef: 'LOC-0237', localTitre: 'Marina Casablanca — Rez-de-chaussée', localVille: 'Casablanca', localSurface: 245, localLoyer: 44000, localFacade: 8, localDisponibilite: 'Immédiate', enseigne: 'Starbucks', secteur: 'Café & Restauration', scoreGlobal: 76, scoreLocalisation: 90, scoreSurface: 80, scoreFacade: 72, scoreBudget: 82, scoreTechnique: 70, scoreTiming: 78, statut: 'À contacter' },
  { localRef: 'LOC-0255', localTitre: 'Palmeraie Mall — RDC Aile Nord', localVille: 'Marrakech', localSurface: 220, localLoyer: 42000, localFacade: 7, localDisponibilite: 'Immédiate', enseigne: 'Yves Rocher', secteur: 'Beauté & Cosmétiques', scoreGlobal: 71, scoreLocalisation: 85, scoreSurface: 75, scoreFacade: 70, scoreBudget: 78, scoreTechnique: 65, scoreTiming: 72, statut: 'À contacter' },
];

// ─── Computed Statistics (derived from shared data) ───────────────────────────

export function computeStats() {
  const totalLocaux = ALL_PROPERTIES.length;
  const publies = ALL_PROPERTIES.filter(p => p.status === 'Publié').length;
  const enRevue = ALL_PROPERTIES.filter(p => p.status === 'En revue').length;
  const brouillons = ALL_PROPERTIES.filter(p => p.status === 'Brouillon').length;
  const enDiscussion = ALL_PROPERTIES.filter(p => p.status === 'En discussion').length;
  const offreRecue = ALL_PROPERTIES.filter(p => p.status === 'Offre reçue').length;
  const enNegociation = ALL_PROPERTIES.filter(p => p.status === 'En négociation').length;
  const visite = ALL_PROPERTIES.filter(p => p.status === 'Visité').length;
  const aFortPotentiel = ALL_PROPERTIES.filter(p => p.status === 'À fort potentiel').length;

  const enseignesEnRecherche = ALL_ENSEIGNES.filter(e => e.statut === 'En recherche').length;
  const totalEnseignes = ALL_ENSEIGNES.length;

  const totalVisites = ALL_VISITES.length;
  const visitesPlanifiees = ALL_VISITES.filter(v => v.statut === 'Planifiée').length;
  const visitesRealisees = ALL_VISITES.filter(v => v.statut === 'Réalisée').length;
  const visitesAnnulees = ALL_VISITES.filter(v => v.statut === 'Annulée').length;
  const visitesAReprogrammer = ALL_VISITES.filter(v => v.statut === 'À reprogrammer').length;

  const totalDocs = ALL_DOCUMENTS.length;
  const docsValides = ALL_DOCUMENTS.filter(d => d.statut === 'Validé').length;
  const docsAVerifier = ALL_DOCUMENTS.filter(d => d.statut === 'À vérifier').length;
  const docsManquants = ALL_DOCUMENTS.filter(d => d.statut === 'Manquant').length;
  const docsExpires = ALL_DOCUMENTS.filter(d => d.statut === 'Expiré').length;

  // Locaux with missing docs = those with hasDocs: false
  const locauxSansDocs = ALL_PROPERTIES.filter(p => !p.hasDocs).length;

  // Locaux not updated in 30+ days (using updatedAt field — simplified: those with old dates)
  const staleLocaux = ALL_PROPERTIES.filter(p =>
    p.updatedAt.includes('fév') || p.updatedAt.includes('jan') || p.updatedAt.includes('02 mars')
  ).length;

  // Matching stats
  const totalMatchs = ALL_MATCHES.length;
  const highScoreMatchs = ALL_MATCHES.filter(m => m.scoreGlobal >= 70).length;
  const tauxMatching = Math.round((highScoreMatchs / totalMatchs) * 100);
  const dealsEnNegociation = ALL_MATCHES.filter(m => m.statut === 'En négociation' || m.statut === 'En discussion').length;

  // Average completude
  const avgCompletude = Math.round(ALL_PROPERTIES.reduce((sum, p) => sum + p.completude, 0) / totalLocaux);

  // Average loyer
  const avgLoyer = Math.round(ALL_PROPERTIES.reduce((sum, p) => sum + p.loyer, 0) / totalLocaux / 1000);

  // By city
  const cities = Array.from(new Set(ALL_PROPERTIES.map(p => p.city)));
  const byCity = cities.map(city => ({
    ville: city,
    locaux: ALL_PROPERTIES.filter(p => p.city === city).length,
    loyer: Math.round(
      ALL_PROPERTIES.filter(p => p.city === city).reduce((s, p) => s + p.loyer, 0) /
      ALL_PROPERTIES.filter(p => p.city === city).length
    ),
  })).sort((a, b) => b.locaux - a.locaux);

  // Statut distribution for pie chart
  const statutDistribution = [
    { name: 'Publié', value: publies + aFortPotentiel, color: '#10b981' },
    { name: 'En discussion', value: enDiscussion, color: '#3b82f6' },
    { name: 'Offre reçue', value: offreRecue, color: '#8b5cf6' },
    { name: 'En négociation', value: enNegociation, color: '#f59e0b' },
    { name: 'Visité', value: visite, color: '#06b6d4' },
    { name: 'Brouillon / En revue', value: brouillons + enRevue, color: '#94a3b8' },
  ].filter(s => s.value > 0);

  // Broker performance derived from visites + properties
  const brokers = ['K. Alami', 'S. Benali', 'N. Tahiri', 'M. Ouali'];
  const brokerPerf = brokers.map(broker => {
    const brokerLocaux = ALL_PROPERTIES.filter(p => p.broker === broker).length;
    const brokerVisites = ALL_VISITES.filter(v => v.broker === broker).length;
    const brokerDeals = ALL_MATCHES.filter(m =>
      ALL_PROPERTIES.find(p => p.ref === m.localRef && p.broker === broker) &&
      (m.statut === 'En discussion' || m.statut === 'En négociation')
    ).length;
    const taux = brokerVisites > 0 ? Math.round((brokerDeals / brokerVisites) * 100) : 0;
    return { broker, locaux: brokerLocaux, visites: brokerVisites, matchs: brokerDeals, taux };
  });

  return {
    totalLocaux,
    publies,
    enRevue,
    brouillons,
    enDiscussion,
    offreRecue,
    enNegociation,
    visite,
    aFortPotentiel,
    enseignesEnRecherche,
    totalEnseignes,
    totalVisites,
    visitesPlanifiees,
    visitesRealisees,
    visitesAnnulees,
    visitesAReprogrammer,
    totalDocs,
    docsValides,
    docsAVerifier,
    docsManquants,
    docsExpires,
    locauxSansDocs,
    staleLocaux,
    totalMatchs,
    highScoreMatchs,
    tauxMatching,
    dealsEnNegociation,
    avgCompletude,
    avgLoyer,
    byCity,
    statutDistribution,
    brokerPerf,
  };
}
